import {useEffect, useRef, useState} from "react";
import {useAuth0} from "@auth0/auth0-react";
import {
    baseUrl,
    baseWSUrl,
    apiVersion,
    enableSuggestions,
    enableAuth,
    enableTTS,
    enabledExtensions,
    defaultSystemPrompt
} from "../config";
import {parse, MathNode, OperatorNode, SymbolNode, ConstantNode, ParenthesisNode, FunctionNode} from "mathjs"
import {v4 as uuidv4} from 'uuid';
import useTTS from "./useTTS";

export class GenerationParams {
    top_k?: number = 0;
    top_p?: number = 1.0;
    temperature?: number = 0.6;
    repetition_penalty?: number = 1.0;
    max_new_tokens?: number = 256;
}

interface Feedback {
    type: string;
    rating: number;
    rating_type: string;
    instruction: string;
    transcript: string;
    output: string;
    source_application: string;
    generation_parameters: GenerationParams;
    model: string;
}

export interface Message {
    text: string;
    role: string;
    modifiedText?: string;
}

export interface UserChats {
    chats: Array<Message>,
    systemPrompt: string
}

export interface UserChatsHistory {
    activeSystemPrompt: string,
    userChats: Array<UserChats>,
    id: string
}

interface AudioSequenceItem {
    audio: any
    played: boolean,
    inProgress: boolean
    playing?: boolean,
    text?: string
}

export interface ErrorMessage {
    type: string,
    message: string,
    errorReturned: string
}

export const API_ERROR_TYPES = {
    INVALID_INPUT: 'invalid_input',
    TOO_MANY_REQUESTS: 'too_many_requests',
    TIMEOUT: 'timeout',
    SYSTEM_OVERLOADED: 'system_overloaded',
    GENERATION_ERROR: 'generation_error',
    INVALID_OUTPUT: 'invalid_output'
}

export const TOKEN_SIZE_LIMIT_ERROR_MESSAGE = "Combination of total input tokens and max_new_tokens exceeded maximum allowed tokens of 16384. Request had 18836 total input tokens and 256 max_new_tokens. Consider reducing the length of your input or decreasing max_new_tokens."

type AudioSequence = Record<number, AudioSequenceItem>;

const defaultSuggestions = [
    "Write a short headline for this conversation.",
    "Generate a summary of this conversation.",
    "Break down this conversation into different subjects and generate a summary for each.",
    "Create a list of emotions that are expressed in this conversation.",
    "How is the emotional tenor of this conversation?",
    "What are the main topics of this conversation?",
]

let defaultChatSystemPrompt =
    "You are \"Nebula\", a Large Language Model trained by Symbl.ai. " +
    "You assist user in performing various tasks from given multi-party human conversation transcripts " +
    "from various domains and settings. You are professional, humble, and helpful.";

const parseSuggestions = (suggestions: string, existingSuggestions: string[] = []) => {
    suggestions.replaceAll(/Prompt [0-9]+:/g, '');
    if (suggestions.includes('I am sorry') ||
        suggestions.includes('not sure') ||
        suggestions.includes('seems to be random') ||
        /(a-zA-Z0-9)+\s*:(a-zA-Z0-9)+/.test(suggestions)) {
        return [];
    }
    suggestions = suggestions.replace('</s>', '');
    suggestions = suggestions.replace('<|endoftext|>', '');
    suggestions = suggestions.replace('<|result|>', '');
    let lines = suggestions.split('\n');
    if (lines.length <= 1) {
        return [];
    }
    // remove leading number or bullet such as '1. ', or '* ', or '- '
    const regex = /^[0-9]+\. |^[*-] /;
    lines = lines.map(line => line.replace(regex, ''));
    // remove empty lines
    lines = lines.filter(line => line.trim() !== '');
    // remove existing suggestions
    lines = lines.filter(line => !existingSuggestions.includes(line));
    // remove duplicates by iterating over the array and removing the first occurrence of each item
    lines = lines.filter((line, index) => lines.indexOf(line) === index);
    // console.log(lines)
    // remove lines that are too long
    lines = lines.filter(line => line.length < 150);
    // remove lines that are too short
    lines = lines.filter(line => line.length > 20);
    // console.log(lines)
    // remove lines that are ending prematurely
    lines = lines.filter(line => {
        return line.endsWith('.') || line.endsWith('?');

    });
    return lines;
}

const formatOutput = (text: string, prompt?: string) => {
    prompt = prompt ? prompt : '';
    text = text.trim();
    text = text.replaceAll('</s>', '');
    text = text.replaceAll('<|endoftext|>', '');
    text = text.replaceAll('<|result|>', '');
    // if (!(prompt.toLowerCase().includes("table") || prompt.toLowerCase().includes("tabular"))) {
    //     text = text.replaceAll('\n', '\n\n');
    // }
    if (!text.includes("```")) {
        if ((text.includes('[') || text.includes('{')) && (prompt.toLowerCase().includes('json') || prompt.toLowerCase().includes('key-value') || prompt.toLowerCase().includes('key value'))) {
            if (text.indexOf('[') >= 0 && (text.indexOf('[') < text.indexOf('{'))) {
                text = text.replace('[', "```json\n[");
                const lastIndex = text.lastIndexOf(']');
                if (lastIndex !== -1) {
                    text = text.slice(0, lastIndex + 1) + '\n```' + text.slice(lastIndex + 1);
                }
            } else {
                text = text.replace('{', "```json\n{");
                const lastIndex = text.lastIndexOf('}');
                if (lastIndex !== -1) {
                    text = text.slice(0, lastIndex + 1) + '\n```' + text.slice(lastIndex + 1);
                }
            }
        } else {
            const lines = text.split('\n')
            for (let i = 0; i < lines.length; i++) {
                // Check if line starts with a bullet or number and if so, add a newline
                if (!lines[i].match(/^[0-9]+\. |^[*-] /)) {
                    if (lines[i].startsWith('|')) {
                        if (i > 0 && !lines[i - 1].trim().startsWith('|')) {
                            lines[i] = '\n' + lines[i].trim();
                        }
                    } else {
                        lines[i] = '\n' + lines[i].trim();
                    }
                }
            }
            text = lines.join('\n');
            // console.log(text)
        }
    }
    return text;
}

const convertToEnglish = (node: MathNode): string => {
    if ((node as OperatorNode).op) {
        let operator = '';
        switch ((node as OperatorNode).op) {
            case '+':
                operator = 'plus';
                break;
            case '-':
                operator = 'minus';
                break;
            case '*':
                operator = 'times';
                break;
            case '/':
                operator = 'divided by';
                break;
            default:
                operator = (node as OperatorNode).op;
                break;
        }

        let args = (node as OperatorNode).args.map(convertToEnglish);
        return args.join(' ' + operator + ' ');
    } else if ((node as SymbolNode).name) {
        return (node as SymbolNode).name;
    } else if ((node as ConstantNode).value !== undefined) {
        return (node as ConstantNode).value.toString();
    } else if ((node as ParenthesisNode).content) {
        return '(' + convertToEnglish((node as ParenthesisNode).content) + ')';
    } else if ((node as FunctionNode).fn) {
        const args = (node as FunctionNode).args.map(convertToEnglish).join(', ');
        const name = (node as FunctionNode).fn.name || 'function'; // Corrected this line
        return `${name} of ${args}`;
    } else {
        throw new Error('Unsupported node type: ' + node.type);
    }
};

const processTextForMathExpressions = (text: string): string => {
    // const regex = /\b(?:\d+|\w+|[\+\-\*\/\(\)])+\b/g;
    const regex = /((-?\d+|\w+)\s?([-+*\/])\s?(-?\d+|\w+)\s?(=\s?(-?\d+|\w+))?)/g;
    const matches = text.match(regex);
    console.log('math matches', matches)
    if (matches) {
        matches.forEach(expression => {
            try {
                const sides = expression.split('=').map(side => side.trim());
                const leftSide = parse(sides[0]);
                const rightSide = parse(sides[1]);
                // const node = parse(expression);
                const englishLeft = convertToEnglish(leftSide);
                const englishRight = convertToEnglish(rightSide);
                text = text.replace(expression, `${englishLeft} equals ${englishRight}`);
            } catch (error) {
                console.error('Failed to parse expression:', expression, error);
            }
        });
    }

    return text;
};

const defaultGenerationParams = {
    top_k: 1,
    top_p: 1.0,
    temperature: 0.0,
    repetition_penalty: 1.0,
    max_new_tokens: 1024,
};

const suggestionsCache = new Map<string, string[]>();

let abortController: AbortController | undefined = undefined;

let audioPoll: any = undefined;

let audioSequence: AudioSequence = {};

let played: number[] = [];

let sentences: string[] = [];

let synthesizeWS: WebSocket|null = null;
const useModel = (generationParams: GenerationParams = defaultGenerationParams, onError: CallableFunction = () => {
                  },
                  onSuccess: CallableFunction = () => {
                  }
) => {
    const [isGenerating, setIsGenerating] = useState(false);
    const [isGeneratingSuggestions, setIsGeneratingSuggestions] = useState(false);
    const [generationParamsState, setGenerationParamState] = useState<GenerationParams>(generationParams);
    const [suggestions, setSuggestions] = useState<string[]>([]);
    const [websocket, setWebsocket] = useState<WebSocket | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [chatMessages, setChatMessages] = useState<Message[]>([]);
    const [chatId, setChatId] = useState<string>(uuidv4());
    const isGeneratingRef = useRef(isGenerating);
    const {getAccessTokenSilently, user} = useAuth0();
    const tts = useTTS();
    const [systemPrompt, setSystemPrompt] = useState<string>("");
    const[activeUserChatIndex, setActiveUserChatIndex] = useState(-1);
    const[userChatsHistory, setUserChatsHistory] = useState<UserChatsHistory[]>([]);


    useEffect(() => {
        isGeneratingRef.current = isGenerating;
    }, [isGenerating]);

    useEffect(() => {
        if (enableAuth) {
            getAccessTokenSilently().then(token => {
                setToken(token);
            });
        }
    }, [getAccessTokenSilently]);

    // useEffect(() => {
    //     if (user?.name && !defaultChatSystemPrompt.includes("The person you are assisting is")) {
    //         defaultChatSystemPrompt = defaultChatSystemPrompt + "\nThe person you're assisting is " + user?.name + ". You are provided this information to help you personalize your responses to the user.";
    //     }
    // }, [user])

    useEffect(() => {
        if (defaultSystemPrompt) {
            setSystemPrompt(defaultSystemPrompt.replaceAll("{{user_name}}", user?.name || "Jake"));
        }
    }, []);

    const updateCurrentSystemPrompt = (systemPrompt: string) => {
        setSystemPrompt(systemPrompt);

        const updatedUserChatsHistory = [...userChatsHistory];
        updatedUserChatsHistory[activeUserChatIndex].activeSystemPrompt = systemPrompt;
        setUserChatsHistory(updatedUserChatsHistory);
    }

    const setGenerationParams = (generationParams?: GenerationParams) => {
        if (!generationParams) {
            generationParams = defaultGenerationParams;
        }

        if (generationParams.repetition_penalty && generationParams.repetition_penalty <= 0.0) {
            generationParams.repetition_penalty = 0.01;
        }

        setGenerationParamState(generationParams);
    }

    const makePostRequest = async (url: string, body: any,
                                   abortController: AbortController | undefined = undefined) => {
        let signal = undefined;
        const bodyWithModelName = {"model": "nebula-chat-large-rc2", ...body}
        if (abortController !== undefined) {
            signal = abortController.signal;
        }
        if (!url) {
            throw Error('URL is empty.');
        }
        if (!url.startsWith('/')) {
            url = '/' + url;
        }
        generationParams = generationParams ? generationParams : generationParamsState;
        const response = await fetch(`${baseUrl}/${apiVersion}${url}`, {
            signal: signal,
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(bodyWithModelName)
        });
        if (response.status === 400) {
            const body = await response.json();
            let message = "One or more input parameters are invalid."
            if (body?.detail) {
                message = body.detail;
            }
            setIsGenerating(false);

            onError({
                type: API_ERROR_TYPES.INVALID_INPUT,
                message: message,
                errorReturned: body?.detail
            });
            return;
        } else if (response.status === 429) {
            let message = "You account has exceeded the maximum number of requests or tokens per minute limit. Please try again in some time."
            setIsGenerating(false);
            onError({
                type: API_ERROR_TYPES.TOO_MANY_REQUESTS,
                message: message,
                errorReturned: body?.detail
            });
            return;
        } else if (response.status === 504) {
            setIsGenerating(false);
            onError({
                type: API_ERROR_TYPES.TIMEOUT,
                message: 'Looks like servers are overloaded due to high traffic. Please wait for some time and try again.',
                errorReturned: body?.detail
            })
        } else if (response.status === 503) {
            setIsGenerating(false);
            onError({
                type: API_ERROR_TYPES.SYSTEM_OVERLOADED,
                message: 'Looks like servers are overloaded due to high traffic. Please wait for some time and try again.',
                errorReturned: body?.detail
            })
            return;
        } else if (response.status === 424) {
            setIsGenerating(false);
            const body = await response.json();
            let message = "Error while generating text. Please try changing parameters or prompt."
            if (body?.detail) {
                message = body.detail;
            }
            onError({
                type: API_ERROR_TYPES.GENERATION_ERROR,
                message: message,
                errorReturned: body?.detail
            })
            return;
        } else if (!response?.ok) {
            setIsGenerating(false);
            onError({
                type: 'invalid_output',
                message: 'Unhandled error was encountered while processing request. Please try again if the problem persists, please contact us at support@symbl.ai.',
                errorReturned: body?.detail
            })
            return;
        }

        if (!response.body) {
            setIsGenerating(false);
            onError({
                type: API_ERROR_TYPES.INVALID_OUTPUT,
                message: 'Response body is empty.',
                errorReturned: body?.detail
            });
            return;
        }
        return response;
    }

    const generate = async (prompt: string, generationParams?: GenerationParams) => {
        const response = await makePostRequest('/model/generate', {
            prompt: prompt,
            ...generationParams ? generationParams : generationParamsState
        })
        if (!response || !response.body) {
            return {
                status: 500,
                data: response?.body ? await response.json() : {
                    detail: 'Error during generation.'
                }
            }
        }
        return {
            status: response.status,
            data: await response.json()
        }
    };

    const generateStreamingWS = async (prompt: string, onGenerated: (event: {
        text: string,
        isFinal: boolean
    }) => void, onError: (error: {
        message: string
    }) => void) => {
        setIsGenerating(true);
        prompt = prompt.trim() + '\n';
        const ws = new WebSocket(`${baseWSUrl}/${apiVersion}/ws/generate?access_token=${token}`);
        setWebsocket(ws);
        ws.addEventListener('open', function (event) {
            ws.send(JSON.stringify({
                action: 'generate',
                prompt: prompt,
                generation_parameters: generationParamsState
            }));
        });

        let text = '';
        let isFinal = false;
        ws.addEventListener('message', function (event) {
                const data = JSON.parse(event.data);
                if (data.type === 'error') {
                    if (data.message === '1') {
                        onError({
                            message: 'Looks like servers are overloaded due to high traffic. Please wait for some time and try again.'
                        });
                    } else {
                        onError(data);
                    }
                    setIsGenerating(false)
                    setWebsocket(null);
                }

                if (data.type === 'status') {
                    if (data.status === 'stopped') {
                        setIsGenerating(false);
                        setWebsocket(null);
                    }
                }

                if (data.type === 'output') {
                    text = formatOutput(data.text, prompt);
                    if (data.is_final) {
                        isFinal = true;
                    }
                    onGenerated({
                        text: text,
                        isFinal: isFinal
                    });
                }

                // if (data.type === 'stats') {
                //     if (data.stats) {
                //         console.log('Stats', data.stats);
                //     }
                // }
            }
        );

        ws.addEventListener('close', function (event) {
            // console.log('WebSocket is closed.', event);
            setIsGenerating(false);
            setWebsocket(null);
        });

        ws.addEventListener('error', function (event) {
            onError({
                message: 'Looks like servers are overloaded due to high traffic. Please wait for some time and try again.'
            });
            setIsGenerating(false);
            setWebsocket(null);
        });
    }

    const streamingResponseIterator = async function* (response: Response, prompt?: string) {
        if (!response.body) {
            return;
        }
        const reader = response.body.getReader();
        let text = '';
        let isFinal = false;
        while (isGeneratingRef.current) {
            const {done, value} = await reader.read();
            text = new TextDecoder("utf-8").decode(value);
            // console.log(text)
            if (text.startsWith(": ping -")) {
                continue;
            }
            let boundary = text.lastIndexOf('\r\n');
            // if (boundary !== -1) {
            let chunk = undefined;
            if (boundary !== -1) {
                chunk = text.slice(0, boundary).trim();
            } else {
                chunk = text.trim();
            }

            text = text.slice(boundary + 1);
            // Split each line on 'data: '
            let lines = chunk.split('\r\n')
                .map(line => line.startsWith('data:') ? line.slice(5).trim() : line.trim());

            lines = lines.filter(line => line.length > 0);
            let lastLine = undefined;
            for (let i = 0; i < lines.length; i++) {
                let line = lines[i];
                // Parse JSON
                try {
                    if (lastLine) {
                        line = lastLine + line;
                    }
                    const output = JSON.parse(line)
                    if (output.text && typeof output.text === 'string') {
                        text = output.text.trim();
                        if (text.length > 0) {
                            // text = text.replaceAll('\n', '\n\r');
                            text = formatOutput(text, prompt);
                            let modifiedHumanText = undefined;
                            if (output.modified_human_text) {
                                modifiedHumanText = output.modified_human_text;
                            }
                            yield {
                                text: text,
                                modifiedHumanText: modifiedHumanText
                            };
                        }
                    }
                    isFinal = output.is_final;
                    if (lastLine) {
                        lastLine = undefined;
                    }
                } catch (e) {
                    if (e instanceof SyntaxError) {
                        lastLine = line;
                    }
                }
            }
            // } else {
            //     text = text.startsWith('data:') ? text.slice(5).trim() : text.trim()
            //     console.log('Boundary not found: ', text)
            // }
            // console.log(text)


            if (done || isFinal) {
                setIsGenerating(false)
                abortController = undefined;
                break;
            }
        }
    }


    const generateStreaming = async function* (prompt: string) {
        try {
            setIsGenerating(true);
            prompt = prompt.trim() + '\n';
            abortController = new AbortController();
            const response = await makePostRequest('/model/generate/streaming', {
                prompt: prompt,
                ...generationParamsState
            }, abortController)
            if (!response || !response.body) {
                return;
            }
            const reader = response.body.getReader();
            let text = '';
            let isFinal = false;
            while (isGeneratingRef.current) {
                const {done, value} = await reader.read();
                text = new TextDecoder("utf-8").decode(value);
                // console.log(text)
                if (text.startsWith(": ping -")) {
                    continue;
                }
                let boundary = text.lastIndexOf('\r\n');
                // if (boundary !== -1) {
                let chunk = undefined;
                if (boundary !== -1) {
                    chunk = text.slice(0, boundary).trim();
                } else {
                    chunk = text.trim();
                }

                text = text.slice(boundary + 1);
                // Split each line on 'data: '
                let lines = chunk.split('\r\n')
                    .map(line => line.startsWith('data:') ? line.slice(5).trim() : line.trim());

                lines = lines.filter(line => line.length > 0);
                let lastLine = undefined;
                for (let i = 0; i < lines.length; i++) {
                    let line = lines[i];
                    // Parse JSON
                    try {
                        if (lastLine) {
                            line = lastLine + line;
                        }
                        const output = JSON.parse(line)
                        if (output.text && typeof output.text === 'string') {
                            text = output.text.trim();
                            if (text.length > 0) {
                                // text = text.replaceAll('\n', '\n\r');
                                text = formatOutput(text, prompt);
                                yield text;
                            }
                        }
                        isFinal = output.is_final;
                        if (lastLine) {
                            lastLine = undefined;
                        }
                    } catch (e) {
                        if (e instanceof SyntaxError) {
                            lastLine = line;
                        }
                    }
                }
                // } else {
                //     text = text.startsWith('data:') ? text.slice(5).trim() : text.trim()
                //     console.log('Boundary not found: ', text)
                // }
                // console.log(text)


                if (done || isFinal) {
                    setIsGenerating(false)
                    abortController = undefined;
                    break;
                }
            }
        } catch
            (e: any) {
            setIsGenerating(false)
            if (e instanceof DOMException) {
                if (e.name === 'AbortError') {
                    console.debug('Generation aborted.')
                    return;
                }
            }
            if (e.message === "Error while parsing JSON output from server.") {
                onError({
                    type: 'invalid_output',
                    message: e.message
                })
            } else {
                console.error('Error from server: ', e)
                onError(e);
            }
        }
    }

    const generateSuggestions = async (transcript: string) => {
        setIsGeneratingSuggestions(true)
        if (suggestionsCache.has(transcript)) {
            setSuggestions(suggestionsCache.get(transcript)!)
            setIsGeneratingSuggestions(false)
            return;
        }
        try {
            setSuggestions([])
            let parsed: string[] = []
            //
            generationParams = {
                max_new_tokens: 256,
                temperature: 0.6,
                top_p: 0.95
            }
            if (enableSuggestions) {
                let response = await generate(
                    `What questions can be asked based on this conversation?\n${transcript}`,
                    generationParams);
                if (response.status === 200) {
                    parsed = parseSuggestions(response.data.output.text, parsed);
                } else {
                    setIsGeneratingSuggestions(false)
                    setIsGenerating(false);
                    const body = await response.data;
                    let message = "One or more input parameters are invalid."
                    if (body?.detail) {
                        message = body.detail;
                    }
                    onError({
                        type: 'invalid_input',
                        message: message
                    });

                    // if (response.status === 400) {
                    //     errorMessage = "Total token length of instruction, transcript, and Max New Tokens (output tokens) exceeded the maximum length of 4096. Please try again with a shorter transcript or reduce Max New Tokens."
                    // }
                    // throw Error(errorMessage);
                }
            }
            // let response = await generate(
            //     "What questions can be asked based on this conversation?",
            //     generationParams
            // );
            // if (response.status === 200) {
            //     console.log('Response: ', response.data.output)
            //     parsed = parseSuggestions(response.data.output, parsed);
            // }

            // Randomly select 5 suggestions from the default suggestions and parsed suggestions
            let suggestions = defaultSuggestions.concat(parsed).sort(() => Math.random() - Math.random())
            suggestionsCache.set(transcript, [...suggestions])
            setSuggestions(suggestions);
            setIsGeneratingSuggestions(false)
            return suggestions;
        } catch (e) {
            setIsGeneratingSuggestions(false)
            onError(e);
            throw e;
        }
    }

    const stopGeneration = () => {
        if (websocket) {
            if (websocket.readyState === WebSocket.OPEN) {
                websocket.send(JSON.stringify({
                    action: 'stop'
                }));
            }
        } else {
            if (abortController) {
                abortController.abort("GenerationStopped");
                abortController = undefined;
            }
            setIsGenerating(false);
        }
    }

    const submitRating = async (rating: number, instruction: string, transcript: string, output: string) => {
        if (rating >= 0 && rating <= 1) {
            const feedback: Feedback = {
                rating: rating,
                rating_type: 'boolean',
                type: 'survey',
                instruction: instruction,
                transcript: transcript,
                generation_parameters: generationParamsState,
                model: 'nebula',
                source_application: 'model-playground',
                output: output
            }
            try {
                const response = await fetch(`${baseUrl}/v1/feedback/language_model`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(feedback),
                });
                if (response.status !== 200) {

                }
                const result = {
                    status: response.status,
                    data: await response.json()
                }
                onSuccess(result);
                return result;
            } catch (e) {
                onError(e);
                console.error(e)
            }
        }
    }

    const getTokenCount = async (text: string) => {
        try {
            const response = await fetch(`${baseUrl}/${apiVersion}/tokenize`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    text: text,
                    return_only_token_count: true
                })
            });
            if (response.status !== 200) {
                console.error(response)
                onError(response);
                return;
            }
            const result = await response.json();
            return result.token_count;
        } catch (e) {
            onError(e);
            console.error(e)
        }
    }

    const resetSuggestions = () => {
        setSuggestions([]);
    }

    const generateChat = async (messages?: Message[], generationParams?: GenerationParams) => {
        if (messages === undefined) {
            messages = chatMessages;
        }
        try {
            setChatMessages([...messages])
            setIsGenerating(true)
            const response = await makePostRequest('/model/chat', {
                messages: messages,
                ...generationParams ? generationParams : generationParamsState
            })
            if (!response || !response.body) {
                return;
            }
            const body = await response.json()
            setChatMessages(body.messages)
            setIsGenerating(false)
            return body.messages;
        } catch (e) {
            console.error(e)
            onError(e);
            setIsGenerating(false)
        }
    }

    const generateChatStreaming = async (message: Message) => {

        const userChatsLength = userChatsHistory[activeUserChatIndex].userChats.length - 1;

        const lastActiveSystemPrompt = userChatsHistory[activeUserChatIndex].userChats[userChatsLength]?.systemPrompt;

        const updatedUserChatsHistory = [...userChatsHistory];

        let messages: Message[];

        if (userChatsLength === -1) {
            // first system prompt
            messages = [message];


            updatedUserChatsHistory[activeUserChatIndex].userChats.push({systemPrompt, chats: messages});
            setUserChatsHistory(updatedUserChatsHistory);
        } else if (systemPrompt !== lastActiveSystemPrompt) {
            messages = [message];

            const userChat: UserChats = {systemPrompt, chats: messages}
            updatedUserChatsHistory[activeUserChatIndex].userChats.push(userChat);
            // !!!!!!! different system prompt
            setUserChatsHistory(updatedUserChatsHistory);
        } else {


            const userChatsLength = updatedUserChatsHistory[activeUserChatIndex].userChats.length;
            const activeUserChatsLength = userChatsLength === 0 ? 0 : userChatsLength - 1;

            messages = [...updatedUserChatsHistory[activeUserChatIndex].userChats[activeUserChatsLength].chats, message]

            updatedUserChatsHistory[activeUserChatIndex].userChats[activeUserChatsLength].chats = messages;
            // same system prompt
            setUserChatsHistory(updatedUserChatsHistory);
        }

        const abortController = new AbortController();
        try {
            let extensions = undefined;
            if (enabledExtensions && Object.keys(enabledExtensions).length > 0) {
                extensions = enabledExtensions;
            }
            setIsGenerating(true)
            const response = await makePostRequest('/model/chat/streaming', {
                chat_id: extensions ? chatId : undefined,
                extensions,
                system_prompt: systemPrompt ? systemPrompt : defaultChatSystemPrompt,
                messages: messages,
                ...generationParamsState
            }, abortController)
            if (!response || !response.body) {
                return;
            }
            const lastHumanMessage = messages[messages.length - 1].text
            messages = [...messages, {
                role: 'assistant',
                text: ''
            }]

            let count = 0;

            for await (const output of streamingResponseIterator(response, lastHumanMessage)) {
                const {text, modifiedHumanText} = output;
                count = count + 1;
                if (modifiedHumanText) {
                    messages[messages.length - 2].modifiedText = modifiedHumanText
                }
                messages[messages.length - 1].text = text
                // if (enableTTS && count % 50 === 0) {
                //     console.log('Calling generateAudio', count, count % 50)
                //     generateAudio(messages[messages.length - 1].text)
                // }

                const updatedUserChatsHistory = [...userChatsHistory];
                const userChatLength = updatedUserChatsHistory[activeUserChatIndex].userChats.length - 1;
                updatedUserChatsHistory[activeUserChatIndex].userChats[userChatLength].chats = messages;
                setUserChatsHistory(updatedUserChatsHistory);

                enableTTS && tts.synthesizeStreaming(messages[messages.length - 1].text, true)
            }
            setIsGenerating(false)
            enableTTS && tts.reset();
            // enableTTS && generateAudio(messages[messages.length - 1].text)
        } catch (e) {
            console.error(e)
            onError(e);
            setIsGenerating(false)
        }
    }

    const stripEmojis = (str: string) =>
        str
            .replace(
                /([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g,
                ''
            )
            .replace(/\s+/g, ' ')
            .trim();


    const generateAudio = (text: string) => {
        text = text.replaceAll('AI ', 'A I ');
        text = text.replaceAll('Symbl.ai', 'Symbl dot A I');
        text = processTextForMathExpressions(text);
        let _sentences = text.split(/(?<!\d)\.\s/);
        // Strip out empty sentences
        _sentences = _sentences.filter((sentence) => sentence.trim().length > 0);
        // Remove list numbers like 1. 2. 3. etc.
        _sentences = _sentences.map((sentence) => sentence.replace(/^\d+\.\s/, ''));
        // Remove pattern "conversation_id: <cid>"
        _sentences = _sentences.map((sentence) => sentence.replace(/\(conversation_id:[0-9]+\)/, ''));
        _sentences = _sentences.map((sentence) => stripEmojis(sentence));
        console.log(`"${_sentences[_sentences.length - 1]}"`)

        if (_sentences.length <= 0) {
            return
        }

        // if (_sentences.length === 1 && (!_sentences[0].endsWith('.') || !_sentences[0].endsWith('?') || !_sentences[0].endsWith('!'))) {
        //     return
        // }
        // console.log(lastSentenceLength, _sentences[_sentences.length - 1].length)
        // console.log(!(_sentences[_sentences.length - 1].endsWith('.') ||
        //     _sentences[_sentences.length - 1].endsWith('?') ||
        //     _sentences[_sentences.length - 1].endsWith('!')))
        if (_sentences.length >= 1 && !(_sentences[_sentences.length - 1].endsWith('.') ||
            _sentences[_sentences.length - 1].endsWith('?') ||
            _sentences[_sentences.length - 1].endsWith('!'))
        ) {
            return;
        }

        const words = _sentences[_sentences.length - 1].split(' ')
        if (!words[words.length - 1].match(/(?<!\d)[.?!]$/) && !['.', '?', '!'].includes(words[words.length - 1][words[words.length - 1].length - 1].trim())) {
            console.log(words[words.length - 1])
            return
        }


        // _sentences = _sentences.map((sentence) => !sentence.endsWith('.') ? sentence + '.' : sentence);
        _sentences = _sentences.filter((sentence) => sentence.trim() !== '.');

        // concatenate consequtive sentences if the total words after concatenation is less than 50, and form a new string.
        // Keep doing this until all sentences are processed. Add this to new array of sentences with newly formed strings.

        let newSentences: string[] = [];
        let newSentence = '';
        for (let i = 0; i < _sentences.length; i++) {
            console.log(i, newSentence.split(' ').length + _sentences[i].split(' ').length, _sentences[i])
            if (
                (newSentence.split(' ').length + _sentences[i].split(' ').length > 25) ||
                i === _sentences.length - 1
                ) {
                if (!newSentence.endsWith('.') && !newSentence.endsWith('?') && !newSentence.endsWith('!')) {
                    newSentence = newSentence + '.';
                }
                if(newSentence.startsWith(".")) {
                    newSentence = newSentence.slice(1);
                }
                newSentence = newSentence.trim();
                if (i === _sentences.length - 1) {
                    newSentence = newSentence + ' ' + _sentences[i];
                }
                console.log('Pushing', newSentence)
                newSentences.push(newSentence);
                newSentence = _sentences[i];
            } else {
                newSentence = newSentence + '. ' + _sentences[i];
                console.log('newSentence', newSentence)
            }
        }

        newSentences = newSentences.filter((sentence) => sentence.trim().length > 0);

        // Replace or append if string from _sentences is not present in sentences
        for (let i = 0; i < newSentences.length; i++) {
            const index = sentences.indexOf(newSentences[i]);
            if (index !== -1) {
                sentences[index] = newSentences[i];
            } else {
                sentences.push(newSentences[i]);
            }
        }

        console.log(sentences)


        if (!audioPoll) {
            audioSequence = {};
            played = [];
        }
        if (sentences.length > Object.keys(audioSequence).length) {
            for (let i = 0; i < sentences.length; i++) {
                if (!Object.keys(audioSequence).includes(i.toString())) {
                    let text = sentences[i];
                    if (!text.endsWith('?') || !text.endsWith('!')) {
                        text += '.';
                    }
                    audioSequence[i] = {
                        audio: null,
                        played: false,
                        inProgress: false,
                        text: text
                    };
                }
            }
        }

        if (!audioPoll) {
            audioPoll = setInterval(() => {
                for (let i = 0; i < Object.keys(audioSequence).length; i++) {
                    if (audioSequence[i].played) {
                        if (!played.includes(i))
                            played.push(i);
                    }
                }
                console.log(played)
                if (played.length < Object.keys(audioSequence).length) {
                    for (let i = played.length; i < Math.min(Object.keys(audioSequence).length, played.length + 3); i++) {
                        if (!audioSequence[i].audio && !audioSequence[i].inProgress) {
                            audioSequence[i] = {
                                audio: null,
                                played: false,
                                inProgress: true
                            };
                            console.log('Calling for', i, sentences[i])
                            synthesizeStreaming(sentences[i], (audio) => {
                                console.log('Audio generated for ', i)
                                audioSequence[i] = {
                                    audio: audio,
                                    played: false,
                                    inProgress: false
                                };
                            });
                        }
                    }
                    const next = played.length;
                    if (audioSequence[next].audio && !audioSequence[next].played && !audioSequence[next].playing) {
                        console.log('playing', next)
                        audioSequence[next].playing = true;
                        playAudio(audioSequence[next].audio, () => {
                            audioSequence[next].played = true;
                            audioSequence[next].playing = false;
                            played.push(next);
                        });
                    }
                }
                if (played.length >= Object.keys(audioSequence).length) {
                    clearInterval(audioPoll);
                    audioPoll = undefined;
                    audioSequence = {}
                    played = [];
                    sentences = [];
                }
            }, 1000);
        }


    }

    const synthesize = async (text: string) => {
        console.log('synthesize', text);
        // @ts-ignore
        // if(cache[text]) {
        //     console.log('cache hit')
        //     // @ts-ignore
        //     return cache[text];
        // }

        // Call the query API
        const response = await fetch(`http://humbly-vast-gnat.cr8dl.ai/v1/synthesize`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                text: text
            }, null, 2)
        })
        const {audio} = await response.json();
        if (typeof audio[0] === 'number') {
            return audio
        } else {
            return audio[0]
        }
    }

    const synthesizeStreaming = async (text: string, onAudio = (audio: []) => {}) => {
        console.log('synthesize', text);
        // @ts-ignore
        // if(cache[text]) {
        //     console.log('cache hit')
        //     // @ts-ignore
        //     return cache[text];
        // }


        const send = (ws: WebSocket, stream: boolean = false) => {
            if (stream) {
                text.split(' ').forEach((word) => {
                    setTimeout( () => {
                        ws.send(JSON.stringify({
                            'text': word
                        }));
                    }, 1000)
                });
            } else {
                ws.send(JSON.stringify({
                    'text': text
                }));
            }

        }
        console.log('synthesizeWS', synthesizeWS, synthesizeWS?.readyState)
        if (synthesizeWS == null || synthesizeWS.readyState !== WebSocket.OPEN) {
            const _ws = new WebSocket(`ws://humbly-vast-gnat.cr8dl.ai/v1/synthesize/streaming`);
            synthesizeWS = _ws;
            console.log('new websocket')
            _ws.addEventListener('open', function (event) {
                console.log('websocket open')
                if (_ws !== null) {
                    send(_ws);
                }
            });

            _ws.addEventListener('message', function (event) {
                const data = JSON.parse(event.data);
                const audio = data.audio;
                if (typeof audio[0] === 'number') {
                    onAudio(data.audio);
                } else {
                    onAudio(data.audio[0]);
                }
            });

            _ws.addEventListener('error', function (event) {
                console.log('websocket error', event)
            });

        } else if (synthesizeWS.readyState === WebSocket.OPEN) {
            console.log('websocket is already open')
            send(synthesizeWS);
        } else {
            console.error('Problem in websocket connection')
        }
        return synthesizeWS;
    };


    const playAudio = (audio: any, onEnd = () => {}, sampleRate = 16000) => {
        const audioContext = new AudioContext();
        const audioBuffer = audioContext.createBuffer(1, audio.length, sampleRate);
        audioBuffer.getChannelData(0).set(audio);
        const audioSource = audioContext.createBufferSource();
        audioSource.buffer = audioBuffer;
        audioSource.onended = onEnd;
        audioSource.connect(audioContext.destination);
        audioSource.start();
    }

    const createNewUserChat = (systemPrompt: string) => {
        setSystemPrompt(systemPrompt);

        const chatId = `${Math.floor(Math.random()*90000) + 10000}`;
        setUserChatsHistory(userChatsHistory => ([{activeSystemPrompt: systemPrompt, userChats: [], id: chatId}, ...userChatsHistory]));
        setActiveUserChatIndex(0);
    }

    const changeActiveUserChat = (index: number) => {
        const userChatHistory = userChatsHistory[index];
        const systemPrompt = userChatsHistory[index].activeSystemPrompt;

        userChatsHistory.splice(index, 1);
        setUserChatsHistory([userChatHistory, ...userChatsHistory]);

        setActiveUserChatIndex(0);
        setSystemPrompt(systemPrompt)
    }

    const deselectActiveUserChat = () => {
        setActiveUserChatIndex(-1)
    }

    return {
        generate,
        generateStreaming,
        generateStreamingWS,
        stopGeneration,
        isGenerating,
        generationParamsState,
        setGenerationParams,
        suggestions,
        generateSuggestions,
        isGeneratingSuggestions,
        submitRating,
        resetSuggestions,
        getTokenCount,
        generateChat,
        generateChatStreaming,
        createNewUserChat,
        activeUserChatIndex,
        changeActiveUserChat,
        deselectActiveUserChat,
        updateCurrentSystemPrompt,
        currentSystemPrompt: systemPrompt,
        userChatsHistory
    };


}

export default useModel;
